import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Factory, 
  HardHat, 
  Wrench, 
  ChevronRight, 
  Phone, 
  MapPin, 
  Mail, 
  Star, 
  Menu, 
  X, 
  MessageCircle, 
  Send,
  CheckCircle,
  Building2,
  Clock,
  Shield,
  Globe
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "./components/ui/select";
import { Toaster, toast } from "sonner";
import { translations } from "./translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved || "es";
  });

  const t = translations[language];

  const toggleLanguage = () => {
    const newLang = language === "es" ? "en" : "es";
    setLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Logo URL from user assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_7c7e2405-8ee6-4604-8880-1545da9da29f/artifacts/3y1gpwne_GRUPO%20ARISTA.png";

// Gallery Images
const GALLERY_IMAGES = [
  { url: "/images/cocina.png" },
  { url: "/images/closet-1.png" },
  { url: "/images/canceleria-exterior.png" },
  { url: "/images/pergola.png" },
  { url: "/images/mampara.png" },
];

// Testimonial images
const TESTIMONIAL_IMAGES = [
  "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
];

// Solution images
const SOLUTION_IMAGES = {
  aristaq: "/images/cocina-2.png",
  aristac: "/images/estructuras.png",
  aristam: "/images/roof.png"
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: t.nav.home, href: "#hero" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.solutions, href: "#solutions" },
    { label: t.nav.gallery, href: "#gallery" },
    { label: t.nav.contact, href: "#contact" },
  ];

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "header-scrolled py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href="#hero" data-testid="logo-link" className="flex items-center">
          <img 
            src={LOGO_URL} 
            alt="Grupo Arista" 
            className="h-12 md:h-16 w-auto"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="font-oswald text-sm uppercase tracking-widest text-white/80 hover:text-arista-cloud transition-colors"
            >
              {item.label}
            </a>
          ))}
          
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            data-testid="language-toggle"
            className="flex items-center gap-2 text-white/80 hover:text-arista-cloud transition-colors border border-white/20 px-3 py-1.5 hover:border-arista-cloud"
          >
            <Globe size={16} />
            <span className="font-oswald text-sm uppercase">{language === "es" ? "EN" : "ES"}</span>
          </button>
          
          <a
            href="#contact"
            data-testid="nav-cta"
            className="btn-silver text-sm"
          >
            {t.nav.quote}
          </a>
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 md:hidden">
          <button
            onClick={toggleLanguage}
            data-testid="mobile-language-toggle"
            className="flex items-center gap-1 text-white/80 hover:text-gold transition-colors"
          >
            <Globe size={18} />
            <span className="font-oswald text-xs uppercase">{language === "es" ? "EN" : "ES"}</span>
          </button>
          <button
            data-testid="mobile-menu-btn"
            className="text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu md:hidden absolute top-full left-0 right-0 py-6">
          <nav className="flex flex-col items-center gap-6">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className="font-oswald text-lg uppercase tracking-widest text-white/80 hover:text-gold transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="mobile-nav-cta"
              className="btn-silver mt-4"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t.nav.quote}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();
  
  return (
    <section 
      id="hero" 
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(/images/hero-pool.png)` 
        }}
      />
      <div className="absolute inset-0 hero-overlay" />
      <div className="absolute inset-0 noise-overlay" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
        <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
          <span className="font-oswald text-arista-cloud text-sm md:text-base uppercase tracking-[0.3em] mb-6 block">
            {t.hero.location}
          </span>
        </div>
        
        <h1 
          className="font-oswald text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-tight mb-6 animate-fade-up"
          style={{ animationDelay: '0.4s' }}
        >
          {t.hero.title1}<br />
          <span className="silver-gradient-text">{t.hero.title2}</span>
        </h1>
        
        <p 
          className="font-manrope text-base md:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto mb-10 animate-fade-up"
          style={{ animationDelay: '0.6s' }}
        >
          {t.hero.subtitle}
        </p>
        
        <div 
          className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up"
          style={{ animationDelay: '0.8s' }}
        >
          <a href="#contact" data-testid="hero-cta-primary" className="btn-silver inline-flex items-center justify-center gap-2">
            {t.hero.cta1}
            <ChevronRight size={20} />
          </a>
          <a href="#solutions" data-testid="hero-cta-secondary" className="btn-outline-silver inline-flex items-center justify-center">
            {t.hero.cta2}
          </a>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-gold rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const { t } = useLanguage();
  const [counters, setCounters] = useState([0, 0, 0, 0]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);
  
  const statsNumbers = [7, 1568, 2465, 2721];
  const statsSuffixes = ["", "+", "+", "+"];
  const statsLabels = [t.stats.years, t.stats.kitchens, t.stats.closets, t.stats.railings];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          statsNumbers.forEach((num, index) => {
            const duration = 2000;
            const steps = 60;
            const increment = num / steps;
            let current = 0;
            const timer = setInterval(() => {
              current += increment;
              if (current >= num) {
                current = num;
                clearInterval(timer);
              }
              setCounters(prev => {
                const newCounters = [...prev];
                newCounters[index] = Math.floor(current);
                return newCounters;
              });
            }, duration / steps);
          });
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <section 
      ref={sectionRef}
      data-testid="stats-section"
      className="py-20 md:py-28 bg-arista-graphite relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-12">
          <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em]">
            {t.stats.subtitle}
          </span>
          <h2 className="font-oswald text-3xl md:text-4xl font-bold text-white mt-2">
            {t.stats.title}
          </h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {statsNumbers.map((_, index) => (
            <div 
              key={index} 
              data-testid={`stat-${index}`}
              className="text-center"
            >
              <div className="stat-number">
                {statsSuffixes[index] === "+" && "+"}
                {counters[index].toLocaleString()}
              </div>
              <p className="font-manrope text-gray-400 mt-2 text-sm md:text-base">
                {statsLabels[index]}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();
  
  return (
    <section 
      id="about" 
      data-testid="about-section"
      className="py-20 md:py-32 bg-arista-charcoal relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em] block mb-4">
              {t.about.subtitle}
            </span>
            <h2 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase mb-6">
              {t.about.title}
            </h2>
            <div className="line-silver mb-8" />
            <p className="font-manrope text-arista-mist text-base md:text-lg leading-relaxed mb-6">
              <strong className="text-white">Grupo Arista</strong> {t.about.p1}
            </p>
            <p className="font-manrope text-arista-mist text-base md:text-lg leading-relaxed mb-8">
              {t.about.p2}
              <span className="text-arista-cloud font-semibold"> {t.about.highlight}</span> {t.about.p3}
            </p>
            <a 
              href="#solutions" 
              data-testid="about-cta"
              className="btn-outline-silver inline-flex items-center gap-2"
            >
              {t.about.cta}
              <ChevronRight size={18} />
            </a>
          </div>
          
          <div className="relative">
            <div className="img-zoom">
              <img 
                src="/images/paneles.png"
                alt="Grupo Arista - Manufactura Arquitectónica"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-arista-smoke p-6 md:p-8">
              <span className="font-oswald text-4xl md:text-5xl font-bold text-white block">7+</span>
              <span className="font-manrope text-arista-pearl text-sm uppercase tracking-wider">
                {t.stats.years.split(" ")[0]}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Solutions Section
const SolutionsSection = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("aristaq");
  
  const solutionIcons = {
    aristaq: Factory,
    aristac: HardHat,
    aristam: Wrench
  };

  const solutions = ["aristaq", "aristac", "aristam"];

  return (
    <section 
      id="solutions" 
      data-testid="solutions-section"
      className="py-20 md:py-32 bg-arista-slate relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16">
          <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em] block mb-4">
            {t.solutions.subtitle}
          </span>
          <h2 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase mb-4">
            {t.solutions.title}
          </h2>
          <p className="font-manrope text-arista-fog max-w-2xl mx-auto">
            {t.solutions.description}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {solutions.map((key) => (
            <button
              key={key}
              data-testid={`solution-tab-${key}`}
              onClick={() => setActiveTab(key)}
              className={`font-oswald text-sm uppercase tracking-wider px-6 py-3 transition-all duration-300 ${
                activeTab === key
                  ? "bg-arista-cloud text-arista-charcoal"
                  : "bg-transparent border border-white/20 text-white hover:border-arista-cloud"
              }`}
            >
              {t.solutions[key].name} — {t.solutions[key].type}
            </button>
          ))}
        </div>

        {/* Active Solution Content */}
        {solutions.map((key) => {
          const Icon = solutionIcons[key];
          return activeTab === key && (
            <div 
              key={key}
              data-testid={`solution-content-${key}`}
              className="grid md:grid-cols-2 gap-12 items-center animate-fade-in"
            >
              <div className="order-2 md:order-1">
                <div className="flex items-center gap-4 mb-6">
                  <Icon className="w-12 h-12 text-arista-cloud" />
                  <div>
                    <h3 className="font-oswald text-2xl md:text-3xl font-bold text-white uppercase">
                      {t.solutions[key].name}
                    </h3>
                    <span className="font-oswald text-arista-fog text-sm uppercase tracking-wider">
                      {t.solutions[key].type}
                    </span>
                  </div>
                </div>
                <p className="font-manrope text-arista-mist mb-8">
                  {t.solutions[key].description}
                </p>
                <ul className="space-y-3">
                  {t.solutions[key].services.map((service, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-arista-cloud flex-shrink-0 mt-0.5" />
                      <span className="font-manrope text-arista-mist">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <div className="img-zoom">
                  <img 
                    src={SOLUTION_IMAGES[key]}
                    alt={t.solutions[key].name}
                    className="w-full h-[350px] md:h-[450px] object-cover"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => {
  const { t } = useLanguage();
  const icons = [Factory, MapPin, Building2, Shield];
  
  return (
    <section 
      data-testid="why-choose-section"
      className="py-20 md:py-32 bg-arista-charcoal relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16">
          <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em] block mb-4">
            {t.whyChoose.subtitle}
          </span>
          <h2 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase">
            {t.whyChoose.title}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.whyChoose.features.map((item, index) => {
            const Icon = icons[index];
            return (
              <div 
                key={index}
                data-testid={`feature-${index}`}
                className="feature-card"
              >
                <Icon className="w-10 h-10 text-white/60 feature-icon transition-colors mb-6" />
                <h3 className="font-oswald text-xl font-semibold text-white uppercase mb-3">
                  {item.title}
                </h3>
                <p className="font-manrope text-arista-fog text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const { t } = useLanguage();
  
  return (
    <section 
      id="gallery" 
      data-testid="gallery-section"
      className="py-20 md:py-32 bg-arista-graphite relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16">
          <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em] block mb-4">
            {t.gallery.subtitle}
          </span>
          <h2 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase">
            {t.gallery.title}
          </h2>
        </div>

        <div className="bento-grid">
          {GALLERY_IMAGES.map((image, index) => (
            <div 
              key={index}
              data-testid={`gallery-item-${index}`}
              className="bento-item img-zoom relative group"
            >
              <img 
                src={image.url}
                alt={t.gallery.images[index]}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-arista-charcoal/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                <span className="font-oswald text-white text-lg uppercase tracking-wider">
                  {t.gallery.images[index]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        size={16} 
        className={i < rating ? "star-filled fill-current" : "star-empty"} 
      />
    ));
  };

  return (
    <section 
      data-testid="testimonials-section"
      className="py-20 md:py-32 bg-arista-charcoal relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16">
          <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em] block mb-4">
            {t.testimonials.subtitle}
          </span>
          <h2 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase">
            {t.testimonials.title}
          </h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="testimonial-card p-8 md:p-12">
            <div className="flex items-center gap-1 mb-6">
              {renderStars(5)}
            </div>
            <p className="font-playfair text-xl md:text-2xl text-white italic leading-relaxed mb-8">
              "{t.testimonials.items[activeIndex].content}"
            </p>
            <div className="flex items-center gap-4">
              <img 
                src={TESTIMONIAL_IMAGES[activeIndex]}
                alt={t.testimonials.items[activeIndex].name}
                className="w-14 h-14 rounded-full object-cover"
              />
              <div>
                <p className="font-oswald text-white font-semibold">
                  {t.testimonials.items[activeIndex].name}
                </p>
                <p className="font-manrope text-arista-fog text-sm">
                  {t.testimonials.items[activeIndex].role} — {t.testimonials.items[activeIndex].company}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {t.testimonials.items.map((_, index) => (
              <button
                key={index}
                data-testid={`testimonial-dot-${index}`}
                onClick={() => setActiveIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === activeIndex ? "bg-arista-cloud w-8" : "bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Map Section
const MapSection = () => {
  const { t } = useLanguage();
  
  return (
    <section 
      data-testid="map-section"
      className="py-20 md:py-32 bg-arista-slate relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em] block mb-4">
              {t.map.subtitle}
            </span>
            <h2 className="font-oswald text-3xl md:text-4xl font-bold text-white uppercase mb-6">
              {t.map.title}
            </h2>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4">
                <MapPin className="w-6 h-6 text-arista-cloud flex-shrink-0" />
                <div>
                  <p className="font-manrope text-white font-medium">{t.map.address}</p>
                  <p className="font-manrope text-arista-fog">{t.map.addressValue}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="w-6 h-6 text-arista-cloud flex-shrink-0" />
                <div>
                  <p className="font-manrope text-white font-medium">{t.map.phone}</p>
                  <a href="tel:+529841798912" className="font-manrope text-arista-fog hover:text-arista-cloud transition-colors">
                    +52 984 179 8912
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-arista-cloud flex-shrink-0" />
                <div>
                  <p className="font-manrope text-white font-medium">{t.map.schedule}</p>
                  <p className="font-manrope text-arista-fog">{t.map.scheduleValue}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="map-container h-[350px] md:h-[400px]">
            <iframe
              title="Ubicación Grupo Arista"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d119428.16556842024!2d-87.15086899999999!3d20.6296!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8f4e433a83fcc6a9%3A0x4404bb3e9c4a4eab!2sPlaya%20del%20Carmen%2C%20Q.R.%2C%20Mexico!5e0!3m2!1sen!2sus!4v1650000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="dark-map"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success(t.contact.success, {
        style: { background: "#2D2D2D", color: "#F5F5F5", border: "1px solid #707070" }
      });
      setFormData({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (error) {
      toast.error(t.contact.error, {
        style: { background: "#2D2D2D", color: "#F5F5F5", border: "1px solid #ef4444" }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      data-testid="contact-section"
      className="py-20 md:py-32 bg-arista-charcoal relative"
    >
      <div className="absolute inset-0 noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="text-center mb-16">
          <span className="font-oswald text-arista-cloud text-sm uppercase tracking-[0.2em] block mb-4">
            {t.contact.subtitle}
          </span>
          <h2 className="font-oswald text-3xl md:text-4xl lg:text-5xl font-bold text-white uppercase mb-4">
            {t.contact.title}
          </h2>
          <p className="font-manrope text-arista-fog max-w-2xl mx-auto">
            {t.contact.description}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <Input
                type="text"
                placeholder={t.contact.form.name}
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="contact-name"
                className="form-input"
              />
              <Input
                type="email"
                placeholder={t.contact.form.email}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="contact-email"
                className="form-input"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <Input
                type="tel"
                placeholder={t.contact.form.phone}
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                data-testid="contact-phone"
                className="form-input"
              />
              <Select 
                value={formData.project_type} 
                onValueChange={(value) => setFormData({...formData, project_type: value})}
              >
                <SelectTrigger data-testid="contact-project-type" className="form-input">
                  <SelectValue placeholder={t.contact.form.projectType} />
                </SelectTrigger>
                <SelectContent className="bg-arista-gray-light border-white/10">
                  <SelectItem value="residential">{t.contact.form.projectTypes.residential}</SelectItem>
                  <SelectItem value="commercial">{t.contact.form.projectTypes.commercial}</SelectItem>
                  <SelectItem value="renovation">{t.contact.form.projectTypes.renovation}</SelectItem>
                  <SelectItem value="maintenance">{t.contact.form.projectTypes.maintenance}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              placeholder={t.contact.form.message}
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              required
              rows={5}
              data-testid="contact-message"
              className="form-input resize-none"
            />
            <Button 
              type="submit" 
              disabled={isSubmitting}
              data-testid="contact-submit"
              className="btn-silver w-full sm:w-auto"
            >
              {isSubmitting ? t.contact.form.sending : t.contact.form.submit}
            </Button>
          </form>

          <div className="bg-arista-graphite p-8 md:p-10">
            <h3 className="font-oswald text-2xl font-bold text-white uppercase mb-6">
              {t.contact.direct}
            </h3>
            <div className="space-y-6">
              <a 
                href="https://wa.me/529841798912"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-center gap-4 p-4 bg-arista-charcoal/50 hover:bg-arista-charcoal transition-colors group"
              >
                <div className="w-12 h-12 bg-[#25D366] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-oswald text-white font-semibold group-hover:text-arista-cloud transition-colors">{t.contact.whatsapp}</p>
                  <p className="font-manrope text-arista-fog text-sm">+52 984 179 8912</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 p-4 bg-arista-charcoal/50">
                <div className="w-12 h-12 bg-arista-smoke/30 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-arista-cloud" />
                </div>
                <div>
                  <p className="font-oswald text-white font-semibold">{t.contact.emailLabel}</p>
                  <p className="font-manrope text-arista-fog text-sm">ventas@aristagroup.mx</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="font-oswald text-white font-semibold mb-4">{t.contact.follow}</p>
              <div className="flex gap-4">
                <a 
                  href="https://www.facebook.com/profile.php?id=61587998368125" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="social-facebook"
                  className="w-10 h-10 bg-arista-charcoal flex items-center justify-center hover:bg-arista-cloud transition-colors group"
                >
                  <svg className="w-5 h-5 text-white group-hover:text-arista-charcoal" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/aristaq.soluciones/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="social-instagram"
                  className="w-10 h-10 bg-arista-charcoal flex items-center justify-center hover:bg-arista-cloud transition-colors group"
                >
                  <svg className="w-5 h-5 text-white group-hover:text-arista-charcoal" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.tiktok.com/@aristaq.soluciones" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="social-tiktok"
                  className="w-10 h-10 bg-arista-charcoal flex items-center justify-center hover:bg-arista-cloud transition-colors group"
                >
                  <svg className="w-5 h-5 text-white group-hover:text-arista-charcoal" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer 
      data-testid="footer"
      className="py-8 bg-arista-charcoal border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <img src={LOGO_URL} alt="Grupo Arista" className="h-10" />
          <p className="font-manrope text-arista-smoke text-sm text-center">
            {t.footer.copyright}
          </p>
          <p className="font-manrope text-arista-cloud text-sm">
            {t.footer.tagline}
          </p>
        </div>
      </div>
    </footer>
  );
};

// Chat Widget Component
const ChatWidget = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/chat`, formData);
      setMessages(prev => [
        ...prev,
        { type: "user", text: formData.message },
        { type: "bot", text: response.data.response }
      ]);
      setFormData({ ...formData, message: "" });
      setHasStarted(true);
    } catch (error) {
      toast.error(t.chat.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        data-testid="chat-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-28 right-8 z-50 w-14 h-14 bg-arista-graphite border border-arista-smoke/30 flex items-center justify-center hover:bg-arista-cloud hover:border-arista-cloud transition-all group ${isOpen ? 'rotate-0' : ''}`}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white group-hover:text-arista-charcoal" />
        ) : (
          <MessageCircle className="w-6 h-6 text-arista-cloud group-hover:text-arista-charcoal" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chat-widget"
          className="chat-widget bg-arista-graphite border border-white/10"
        >
          <div className="p-4 border-b border-white/10">
            <h3 className="font-oswald text-white font-semibold">{t.chat.title}</h3>
            <p className="font-manrope text-arista-fog text-sm">{t.chat.subtitle}</p>
          </div>

          <div className="h-64 overflow-y-auto p-4 space-y-3">
            {!hasStarted && (
              <div className="bg-arista-charcoal p-3 rounded">
                <p className="font-manrope text-arista-mist text-sm">
                  {t.chat.welcome}
                </p>
              </div>
            )}
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded ${msg.type === 'user' ? 'bg-arista-smoke/20 ml-8' : 'bg-arista-charcoal mr-8'}`}
              >
                <p className={`font-manrope text-sm ${msg.type === 'user' ? 'text-arista-cloud' : 'text-arista-mist'}`}>
                  {msg.text}
                </p>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="p-4 border-t border-white/10 space-y-3">
            {!hasStarted && (
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="text"
                  placeholder={t.chat.namePlaceholder}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="chat-name"
                  className="form-input text-sm h-10"
                />
                <Input
                  type="email"
                  placeholder={t.chat.emailPlaceholder}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="chat-email"
                  className="form-input text-sm h-10"
                />
              </div>
            )}
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder={t.chat.messagePlaceholder}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                required
                data-testid="chat-message"
                className="form-input text-sm h-10 flex-1"
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                data-testid="chat-submit"
                className="bg-arista-cloud hover:bg-arista-pearl text-arista-charcoal h-10 px-3 relative z-50"
              >
                <Send size={18} />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

// WhatsApp Button Component
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/529841798912"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float"
      className="whatsapp-btn animate-pulse-gold"
      title="WhatsApp"
    >
      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
    </a>
  );
};

// Main App Component
function App() {
  return (
    <LanguageProvider>
      <div className="App bg-arista-charcoal min-h-screen">
        <Toaster position="top-right" />
        <Header />
        <main>
          <HeroSection />
          <StatsSection />
          <AboutSection />
          <SolutionsSection />
          <WhyChooseSection />
          <GallerySection />
          <TestimonialsSection />
          <MapSection />
          <ContactSection />
        </main>
        <Footer />
        <ChatWidget />
        <WhatsAppButton />
      </div>
    </LanguageProvider>
  );
}

export default App;
